import { useId, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from '../lib/cn'

/**
 * Form field — ported from `FgField` in `designs/system.jsx` (~577), derived
 * from node `394:1621` (border grey-100, radius 16, padding 21/17).
 *
 * Built generically here because M17-T09 exercises the most states (hint,
 * error, disabled, select, checkbox) and T10/T11 adopt it for the specialists,
 * diagnostics and medicines forms. Resist adding flow-specific props.
 *
 * ACCESSIBILITY DEVIATION, carried over from M16 and deliberate: upstream
 * placeholder ink is `#b0b3b1`, 2.12:1 on white, which fails WCAG AA. `--ink-4`
 * is 3.57:1 and also fails. Placeholders use `--ink-3` (8.03:1). Logged as a
 * gap rather than ported.
 *
 * ERROR COLOUR IS `--bed-bad`, NEVER `--sos`. Both resolve to #b00020 today,
 * which is exactly why this is easy to get wrong. They are separate tokens on
 * purpose: `--sos` means "this is the emergency surface" (ADR-0035), `--bed-bad`
 * means "this is wrong / unavailable". A future palette may split them, and an
 * invalid phone number is not an emergency.
 */
type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'type' | 'disabled'
>

export interface FieldProps extends NativeInputProps {
  /** Visible label. Always visible — this component has no label-less mode. */
  label: string
  /** M17-T11 added the date types for the diagnostics scheduling field. */
  type?:
    | 'text'
    | 'tel'
    | 'email'
    | 'number'
    | 'password'
    | 'date'
    | 'datetime-local'
  /** Supporting copy shown when there is no error. */
  hint?: string
  /** Validation message. Replaces the hint and marks the input invalid. */
  error?: string
  disabled?: boolean
  /** Rendered inside the field shell, after the input — a Send/Verify button. */
  trailing?: ReactNode
  className?: string
}

export function Field({
  label,
  type = 'text',
  hint,
  error,
  disabled,
  trailing,
  className,
  ...rest
}: FieldProps): ReactNode {
  const reactId = useId()
  const id = `field-${reactId}`
  const msgId = `${id}-msg`
  const message = error ?? hint

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <label
        htmlFor={id}
        className="text-[16px] font-medium leading-6 text-ink-1"
      >
        {label}
      </label>

      <div className={cn('flex gap-2', trailing ? 'items-stretch' : undefined)}>
        <input
          {...rest}
          id={id}
          type={type}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? msgId : undefined}
          className={cn(
            // PORTED — radius 16 (--r-4), padding 21/17, --line-1 border.
            'w-full min-w-0 rounded-r3 border bg-bg-app px-[21px] py-[17px] text-[16px] leading-6 text-ink-1 outline-none',
            'placeholder:text-ink-3',
            error ? 'border-bed-bad' : 'border-line-1',
            // DERIVED — Figma draws no disabled state (G31). The native
            // attribute does the real work: it removes the input from the tab
            // order and AT announces it.
            disabled ? 'cursor-not-allowed bg-bg-soft opacity-60' : undefined,
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        />
        {trailing}
      </div>

      {message ? (
        <span
          id={msgId}
          // An error is announced; a hint is not. A hint that interrupts a
          // screen-reader user mid-form is noise.
          role={error ? 'alert' : undefined}
          className={cn(
            'text-[14px] leading-5',
            error ? 'text-bed-bad' : 'text-ink-3',
          )}
        >
          {message}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Checkbox variant. Split out rather than folded into `Field` via a `type`
 * prop: the layout inverts (control before label), there is no shell, and the
 * label is body copy rather than a field name — one component pretending to be
 * both would be all branches.
 */
export interface CheckboxFieldProps {
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
}

export function CheckboxField({
  label,
  checked,
  onChange,
  disabled,
  className,
}: CheckboxFieldProps): ReactNode {
  return (
    <label
      className={cn(
        'flex items-start gap-2 text-[14px] leading-5 text-ink-2',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      {/* A real checkbox, not the 15x10 rectangle the frames draw. */}
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      />
      <span>{label}</span>
    </label>
  )
}
